// One microphone capture for the whole exam, like the shared camera (utils/sharedCamera.js).
// BackgroundAudioRecorder (speaker/talking checks) and VideoInterview (the answer recording) each
// record from their own copy (clone) of it. Opening the microphone twice made phones mute the
// earlier capture: during a video answer the audio checks heard almost nothing, so a second voice
// could not be caught (staging 809413/809418, 27 Sep 2026).

// The video recorder's own audio settings (VideoRecordingConfig), mono.
export const SHARED_MIC_CONSTRAINTS = {
  audio: {
    echoCancellation: true,
    noiseSuppression: true,
    autoGainControl: true,
    channelCount: 1,
  },
  video: false,
};

let micStreamPromise = null;
const copies = new Set();

const liveAudioTrack = (stream) =>
  stream && typeof stream.getAudioTracks === "function"
    ? stream.getAudioTracks().find((track) => track.readyState === "live") || null
    : null;

const getSharedMicStream = async () => {
  if (micStreamPromise) {
    const existing = await micStreamPromise.catch(() => null);
    if (liveAudioTrack(existing)) return existing;
  }
  micStreamPromise = navigator.mediaDevices.getUserMedia(SHARED_MIC_CONSTRAINTS);
  try {
    return await micStreamPromise;
  } catch (err) {
    micStreamPromise = null;
    throw err;
  }
};

// A copy of the shared microphone track. Stop it when done (stopping a copy never stops the
// others), then call releaseSharedMicIfUnused().
export const acquireMicTrack = async () => {
  const track = liveAudioTrack(await getSharedMicStream());
  if (!track) throw new Error("Shared microphone is not live");
  const copy = track.clone();
  copies.add(copy);
  return copy;
};

// Closes the shared capture once no copy is live (so the browser's mic indicator goes off after
// the exam). Safe to call any time.
export const releaseSharedMicIfUnused = async () => {
  for (const copy of Array.from(copies)) {
    if (copy.readyState === "ended") copies.delete(copy);
  }
  if (copies.size > 0 || !micStreamPromise) return;
  const stream = await micStreamPromise.catch(() => null);
  micStreamPromise = null;
  if (stream) stream.getTracks().forEach((track) => track.stop());
};

// Phones and tablets (same test as the video recorder, plus iPads that report a Mac).
export const isPhoneOrTablet = () =>
  /Mobile|iP(hone|od|ad)|Android|BlackBerry|IEMobile/.test(navigator.userAgent) ||
  (/Macintosh/.test(navigator.userAgent) && navigator.maxTouchPoints > 1);

// The audio proctoring checks' microphone. Phones: a copy of the shared capture (opening the mic
// twice mutes the earlier capture there). Desktops: a separate UNPROCESSED capture - Chrome's
// noise suppression and gain control (plus Windows voice processing) push a second, farther voice
// down, so laptops caught far fewer second voices than phones (staging 28 Sep 2026: 24% of laptop
// clips vs 39% of phone clips had a second voice; no laptop session ever warned). The video
// answer keeps the cleaned audio. Falls back to the shared copy if the raw capture fails.
export const acquireCheckMicTrack = async () => {
  if (!isPhoneOrTablet()) {
    try {
      const raw = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: false, noiseSuppression: false, autoGainControl: false, channelCount: 1 },
        video: false,
      });
      const track = liveAudioTrack(raw);
      if (track) return track;
      raw.getTracks().forEach((t) => t.stop());
    } catch (err) {
      console.warn("[sharedMic] Unprocessed microphone not available, using the shared one:", err?.message || err);
    }
  }
  return acquireMicTrack();
};
